import React, { useEffect, useMemo, useRef, useState } from "react"
import {
  GoogleMap,
  useLoadScript,
  Marker,
  DirectionsRenderer,
  TrafficLayer
} from "@react-google-maps/api"
import { Grid } from "@material-ui/core"
import { googleAPiKey } from "../../libs/const"

const TripRouteMap = ({ pickup, drop, driverLoc, zoom = 15 }) => {
  const { isLoaded } = useLoadScript({ googleMapsApiKey: googleAPiKey })
  const [directions, setDirections] = useState(null)
  const [map, setMap] = useState(null)
  const initialCenter = useRef(null)
  const didFitRef = useRef(false)
  const didFocusDriverRef = useRef(false)

  // Snapshot pickup on first load — never change
  if (!initialCenter.current && pickup) {
    initialCenter.current = pickup
  }

  const pickupPos = useMemo(
    () => (pickup ? { lat: pickup.lat, lng: pickup.lng } : null),
    [pickup?.lat, pickup?.lng]
  )
  const dropPos = useMemo(
    () => (drop ? { lat: drop.lat, lng: drop.lng } : null),
    [drop?.lat, drop?.lng]
  )
  const driverPos = useMemo(
    () =>
      driverLoc?.lat != null && driverLoc?.lng != null
        ? { lat: driverLoc.lat, lng: driverLoc.lng }
        : null,
    [driverLoc?.lat, driverLoc?.lng]
  )

  // Route: driver → pickup → drop while the driver is en route (has live loc),
  // otherwise pickup → drop. Recompute whenever live location updates.
  useEffect(() => {
    if (!isLoaded || !pickupPos || !dropPos) { setDirections(null); return }
    const svc = new window.google.maps.DirectionsService()
    const origin = driverPos || pickupPos
    const destination = dropPos
    const waypoints = driverPos
      ? [{ location: pickupPos, stopover: true }]
      : []
    svc.route(
      {
        origin,
        destination,
        waypoints,
        travelMode: window.google.maps.TravelMode.DRIVING,
        unitSystem: window.google.maps.UnitSystem.IMPERIAL,
      },
      (result, status) => {
        if (status === "OK") setDirections(result)
      }
    )
  }, [isLoaded, pickupPos, dropPos, driverPos])

  // Without a live driver (trip not started): fit the route ONCE so pickup +
  // drop are both visible.
  useEffect(() => {
    if (!isLoaded || !map || !window.google || driverPos) return
    const bounds = new window.google.maps.LatLngBounds()
    let has = false
    ;[pickupPos, dropPos].forEach(p => {
      if (p) { bounds.extend(p); has = true }
    })
    if (has && !didFitRef.current) {
      map.fitBounds(bounds, 80)
      didFitRef.current = true
    }
  }, [isLoaded, map, driverPos, pickupPos, dropPos])

  // When On Route (live driver location): focus ON THE DRIVER ONCE on load and
  // zoom in. After that the user can zoom/pan freely — we never fight them
  // (and we don't re-zoom on every live location update).
  useEffect(() => {
    if (!map || !driverPos || didFocusDriverRef.current) return
    map.panTo(driverPos)
    map.setZoom(16) // zoom into the driver, not the whole route
    didFocusDriverRef.current = true
  }, [map, driverPos])

  if (!isLoaded) return <Grid>Loading...</Grid>

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <GoogleMap
        center={driverPos || initialCenter.current || pickupPos}
        zoom={zoom}
        mapContainerStyle={{ width: "100%", height: "100%" }}
        onLoad={m => { setMap(m) }}
        options={{
          zoomControl: true,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
          draggable: true,
          // Enable scroll-wheel + pinch zoom (default "auto" blocks wheel zoom)
          gestureHandling: "greedy"
        }}
      >
        <TrafficLayer />
      {directions && (
        <DirectionsRenderer
          directions={directions}
          options={{
            suppressMarkers: true,
            preserveViewport: true,
            polylineOptions: { strokeColor: "#1E88E5", strokeWeight: 6, strokeOpacity: 0.9 }
          }}
        />
      )}

      {pickupPos && (
        <Marker
          position={pickupPos}
          icon={{ url: require("../../assets/icons/start_marker.svg").default, scale: 7 }}
        />
      )}

      {dropPos && (
        <Marker
          position={dropPos}
          icon={{ url: require("../../assets/icons/end_marker.svg").default, scale: 7 }}
        />
      )}

      {driverPos && (
        <Marker
          position={driverPos}
          icon={{ url: require("../../assets/icons/driverLocation.svg").default, scale: 7 }}
        />
      )}
      </GoogleMap>

      {/* Focus-on-driver control — re-zooms the map to the driver's car */}
      {driverPos && (
        <button
          onClick={() => {
            if (map && driverPos) {
              map.panTo(driverPos)
              map.setZoom(16)
            }
          }}
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            zIndex: 10,
            width: 44,
            height: 44,
            borderRadius: "50%",
            border: "none",
            cursor: "pointer",
            background: "#fff",
            boxShadow: "0 2px 10px rgba(0,0,0,0.25)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
          title="Focus on driver"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="#1a73e8">
            <path d="M12 2a1 1 0 0 1 1 1v2.07A7 7 0 0 1 19 12h2a1 1 0 1 1 0 2h-2.07A7 7 0 0 1 13 19v2a1 1 0 1 1-2 0v-2.07A7 7 0 0 1 5 12H3a1 1 0 1 1 0-2h2.07A7 7 0 0 1 11 5.07V3a1 1 0 0 1 1-1zm0 5a5 5 0 1 0 0 10 5 5 0 0 0 0-10zm0 2a3 3 0 1 1 0 6 3 3 0 0 1 0-6z"/>
          </svg>
        </button>
      )}
    </div>
  )
}

export default TripRouteMap